import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { orderService } from '../../services/order.service';
import { paymentService } from '../../services/payment.service';
import { theme } from '../../config/theme';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { PackageIcon } from '../../components/icons/Icons';

// ─── Sub-components ───────────────────────────────────────────────────────────

const Badge = ({ color, children }) => (
  <span style={{
    display: 'inline-block', padding: '3px 10px', borderRadius: theme.borderRadius.full,
    fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold,
    color: '#fff', backgroundColor: color,
  }}>
    {children}
  </span>
);

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—';
  return <Badge color={color}>{label}</Badge>;
};

const PaymentBadge = ({ method }) => {
  const colors = { razorpay: '#4f46e5', payu: '#0ea5e9', cod: '#78716c' };
  return (
    <Badge color={colors[method] || theme.colors.gray[400]}>
      {method?.toUpperCase() || 'N/A'}
    </Badge>
  );
};

// ─── Refund Modal ─────────────────────────────────────────────────────────────

const RefundModal = ({ order, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isFullRefund, setIsFullRefund] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const maxAmount = parseFloat(order?.payment?.amount || order?.total || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const refundAmount = isFullRefund ? null : parseFloat(amount);

    if (!isFullRefund) {
      if (!amount || isNaN(refundAmount) || refundAmount <= 0) {
        return setError('Enter a valid refund amount.');
      }
      if (refundAmount > maxAmount) {
        return setError(`Amount cannot exceed ₹${maxAmount.toFixed(2)}.`);
      }
    }

    if (!reason.trim() || reason.trim().length < 10) {
      return setError('Please provide a reason (at least 10 characters).');
    }

    setLoading(true);
    try {
      await paymentService.processRefund({
        order_id: order.id,
        amount: refundAmount,
        reason: reason.trim(),
      });
      toast.success('Refund initiated successfully!');
      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Refund failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: theme.zIndex.modal, padding: theme.spacing.lg,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#fff', borderRadius: theme.borderRadius['2xl'],
        padding: theme.spacing['2xl'], maxWidth: 480, width: '100%',
        boxShadow: theme.shadows.xl,
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.xs }}>
          Process Refund
        </h2>
        <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: theme.spacing.xl }}>
          Order <strong>#{order.order_number}</strong> · {formatCurrency(maxAmount)} paid via{' '}
          <strong>{order.payment?.payment_method?.toUpperCase() || 'RAZORPAY'}</strong>
        </p>

        <form onSubmit={handleSubmit}>
          {/* Refund Type Toggle */}
          <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.lg }}>
            {[{ label: 'Full Refund', full: true }, { label: 'Partial Refund', full: false }].map(opt => (
              <button key={opt.label} type="button"
                onClick={() => { setIsFullRefund(opt.full); setAmount(''); setError(''); }}
                style={{
                  flex: 1, padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  borderRadius: theme.borderRadius.lg,
                  border: `2px solid ${isFullRefund === opt.full ? theme.colors.primary.main : theme.colors.gray[200]}`,
                  backgroundColor: isFullRefund === opt.full ? `${theme.colors.primary.main}10` : 'transparent',
                  color: isFullRefund === opt.full ? theme.colors.primary.main : theme.colors.text.secondary,
                  fontWeight: theme.typography.fontWeight.semibold,
                  fontSize: theme.typography.fontSize.sm,
                  cursor: 'pointer', transition: theme.transitions.fast,
                }}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Partial Amount Input */}
          {!isFullRefund && (
            <div style={{ marginBottom: theme.spacing.lg }}>
              <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing.xs }}>
                Refund Amount (₹)
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: theme.spacing.md, top: '50%', transform: 'translateY(-50%)',
                  color: theme.colors.text.secondary, fontWeight: theme.typography.fontWeight.semibold,
                }}>₹</span>
                <input
                  type="number" step="0.01" min="0.01" max={maxAmount}
                  value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder={`Max: ${maxAmount.toFixed(2)}`}
                  style={{
                    width: '100%', padding: `${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md} 2rem`,
                    borderRadius: theme.borderRadius.lg, border: `1px solid ${theme.colors.gray[300]}`,
                    fontSize: theme.typography.fontSize.base, outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          )}

          {/* Reason */}
          <div style={{ marginBottom: theme.spacing.lg }}>
            <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing.xs }}>
              Reason for Refund <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <textarea
              value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. Product defective, customer request, order cancellation..."
              rows={3}
              style={{
                width: '100%', padding: theme.spacing.md,
                borderRadius: theme.borderRadius.lg, border: `1px solid ${theme.colors.gray[300]}`,
                fontSize: theme.typography.fontSize.sm, resize: 'vertical',
                outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
            <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled, marginTop: 4 }}>
              {reason.length} chars (min 10)
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              backgroundColor: '#fef2f2', border: `1px solid #fecaca`,
              borderRadius: theme.borderRadius.md, padding: theme.spacing.md,
              color: theme.colors.error, fontSize: theme.typography.fontSize.sm,
              marginBottom: theme.spacing.lg,
            }}>
              ⚠ {error}
            </div>
          )}

          {/* Summary Box */}
          <div style={{
            backgroundColor: theme.colors.gray[50], borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.md, marginBottom: theme.spacing.xl,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
              Refund to customer
            </span>
            <span style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary.main }}>
              {isFullRefund ? formatCurrency(maxAmount) : (amount ? `₹${parseFloat(amount || 0).toFixed(2)}` : '—')}
            </span>
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.md }}>
            <Button variant="outline" onClick={onClose} style={{ flex: 1 }} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" style={{ flex: 1 }} disabled={loading}>
              {loading ? 'Processing...' : 'Confirm Refund'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Refunds Page ────────────────────────────────────────────────────────

const AdminRefunds = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('delivered'); // UPDATED
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // UPDATED
  const TABS = [
    { key: 'delivered', label: 'Eligible (Delivered)' },
    { key: 'refunded', label: 'Refunded' },
    { key: '', label: 'All Orders' },
  ];

  const fetchOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (activeTab) params.status = activeTab;
      const response = await orderService.getAllOrders(params);
      const data = response.data?.data || response.data;
      setOrders(data.orders || []);
      setPagination({ page: data.pagination?.page || 1, totalPages: data.pagination?.totalPages || 1 });
    } catch (err) {
      console.error(err);
      setOrders([]);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchOrders(1); }, [fetchOrders]);

  const filteredOrders = orders.filter(o => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.user?.email?.toLowerCase().includes(q) ||
      `${o.user?.first_name} ${o.user?.last_name}`.toLowerCase().includes(q)
    );
  });

  // UPDATED
  const canRefund = (order) => order.status === 'delivered' && order.payment?.payment_method !== 'cod';

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? theme.spacing.md : theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: theme.spacing.xl }}>
          <h1 style={{ fontSize: isMobile ? theme.typography.fontSize['2xl'] : theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, margin: 0 }}>
            Refund Management
          </h1>
          <p style={{ color: theme.colors.text.secondary, marginTop: theme.spacing.xs, fontSize: theme.typography.fontSize.sm }}>
            Process full or partial refunds for completed orders. Razorpay Route transfers are automatically reversed.
          </p>
        </div>

        {/* Info Banner */}
        <div style={{
          display: 'flex', gap: theme.spacing.md, alignItems: 'flex-start',
          backgroundColor: '#eff6ff', border: `1px solid #bfdbfe`,
          borderRadius: theme.borderRadius.xl, padding: theme.spacing.lg,
          marginBottom: theme.spacing.xl, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '1.25rem' }}>ℹ️</span>
          <div style={{ flex: 1, fontSize: theme.typography.fontSize.sm, color: '#1d4ed8', lineHeight: 1.6 }}>
            <strong>Smart Refund Engine:</strong> For Razorpay payments with pending seller transfers, the transfer is automatically reversed. For already-settled transfers, the platform absorbs the amount and a deduction is logged for accounting.
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: theme.spacing.md, marginBottom: theme.spacing.lg, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: theme.spacing.sm, overflowX: 'auto' }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                borderRadius: theme.borderRadius.full,
                border: `1px solid ${activeTab === tab.key ? theme.colors.primary.main : theme.colors.gray[300]}`,
                backgroundColor: activeTab === tab.key ? theme.colors.primary.main : 'transparent',
                color: activeTab === tab.key ? '#fff' : theme.colors.text.secondary,
                fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium,
                cursor: 'pointer', whiteSpace: 'nowrap', transition: theme.transitions.fast,
              }}>{tab.label}</button>
            ))}
          </div>

          {/* Search */}
          <input
            type="text" placeholder="Search order # or customer..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.md}`,
              borderRadius: theme.borderRadius.lg, border: `1px solid ${theme.colors.gray[300]}`,
              fontSize: theme.typography.fontSize.sm, outline: 'none',
              minWidth: 220, flex: isMobile ? 1 : undefined,
            }}
          />
        </div>

        {/* Content */}
        {loading ? (
          <LoadingSpinner />
        ) : filteredOrders.length === 0 ? (
          <Card padding="xl">
            <div style={{ textAlign: 'center', padding: theme.spacing['2xl'] }}>
              <PackageIcon size={56} color={theme.colors.gray[300]} />
              <h3 style={{ marginTop: theme.spacing.lg, color: theme.colors.text.secondary }}>No orders found</h3>
              <p style={{ color: theme.colors.text.disabled, fontSize: theme.typography.fontSize.sm }}>
                {searchQuery ? 'Try a different search term.' : 'No orders match the selected filter.'}
              </p>
            </div>
          </Card>
        ) : (
          <>
            <Card padding="0">
              {/* Table Header */}
              {!isMobile && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1.6fr 1fr 1fr 1fr 1fr 120px',
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  backgroundColor: theme.colors.gray[50],
                  borderBottom: `1px solid ${theme.colors.gray[200]}`,
                  fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.text.secondary, textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  <span>Order</span>
                  <span>Customer</span>
                  <span>Total</span>
                  <span>Payment</span>
                  <span>Status</span>
                  <span>Date</span>
                  <span></span>
                </div>
              )}

              {filteredOrders.map((order, idx) => (
                <div key={order.id} style={{
                  display: isMobile ? 'flex' : 'grid',
                  flexDirection: isMobile ? 'column' : undefined,
                  gridTemplateColumns: isMobile ? undefined : '1.2fr 1.6fr 1fr 1fr 1fr 1fr 120px',
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  borderBottom: idx < filteredOrders.length - 1 ? `1px solid ${theme.colors.gray[100]}` : 'none',
                  alignItems: 'center', gap: isMobile ? theme.spacing.sm : 0,
                  opacity: !canRefund(order) && order.status !== 'refunded' ? 0.65 : 1,
                }}>
                  {/* Order Number */}
                  <div>
                    <div style={{ fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.sm }}>
                      #{order.order_number}
                    </div>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>
                      {order.items?.length || 0} item(s)
                    </div>
                  </div>

                  {/* Customer */}
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                    {order.user ? `${order.user.first_name} ${order.user.last_name}` : 'Unknown'}
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled }}>
                      {order.user?.email}
                    </div>
                  </div>

                  {/* Total */}
                  <div style={{ fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.sm }}>
                    {formatCurrency(order.total)}
                    {order.payment?.refund_amount && (
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.success }}>
                        Refunded: {formatCurrency(order.payment.refund_amount)}
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div>
                    <PaymentBadge method={order.payment?.payment_method} />
                    {order.payment?.payment_method === 'cod' && (
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.disabled, marginTop: 2 }}>
                        Not refundable
                      </div>
                    )}
                  </div>

                  {/* Order Status */}
                  <div><StatusBadge status={order.status} /></div>

                  {/* Date */}
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                    {formatDate(order.createdAt || order.created_at)}
                  </div>

                  {/* Action */}
                  <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                    {order.status === 'refunded' ? (
                      <Badge color={theme.colors.success}>Refunded</Badge>
                    ) : canRefund(order) ? (
                      <Button
                        variant="primary" size="sm"
                        onClick={() => setSelectedOrder(order)}
                      >
                        Refund
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled>
                        {order.payment?.payment_method === 'cod' ? 'COD' : '—'}
                      </Button>
                    )}
                    <Button
                      variant="outline" size="sm"
                      onClick={() => navigate(`/admin/orders/${order.id}`)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </Card>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.lg }}>
                <Button variant="outline" size="sm" disabled={pagination.page <= 1}
                  onClick={() => fetchOrders(pagination.page - 1)}>Previous</Button>
                <span style={{ alignSelf: 'center', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchOrders(pagination.page + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Refund Modal */}
      {selectedOrder && (
        <RefundModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onSuccess={() => {
            setSelectedOrder(null);
            fetchOrders(pagination.page);
          }}
        />
      )}
    </MainLayout>
  );
};

export default AdminRefunds;